/**
 * Learning Service
 * 
 * API calls for courses, videos, materials, and blog.
 */
import api from '@/lib/api';
import type {
  CourseDetail,
  CoursesListResponse,
  Lesson,
  MaterialsListResponse,
  BlogPostsListResponse,
  BlogPost,
  BlogCategoryOption,
  BlogHighlightsResponse,
  VideosListResponse,
  VideoDetail,
  VideoPlayback,
} from '@/types/learning';

// Reusa a instância `api` compartilhada (@/lib/api): baseURL já inclui /api/v1
// e traz os interceptors de autenticação e refresh de token do app. Por isso os
// caminhos abaixo são relativos a /api/v1 (ex.: /learn/materials).
export const learningService = {
  // Courses
  getCourses: async (
    search?: string,
    page: number = 1,
    pageSize: number = 12
  ): Promise<CoursesListResponse> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    
    const response = await api.get(`/learn/courses?${params.toString()}`);
    return response.data;
  },

  getCourseDetails: async (courseId: string): Promise<CourseDetail> => {
    const response = await api.get(`/learn/courses/${courseId}`);
    return response.data;
  },

  getLesson: async (courseId: string, lessonId: string): Promise<Lesson> => {
    const response = await api.get(`/learn/courses/${courseId}/lessons/${lessonId}`);
    return response.data;
  },

  updateLessonProgress: async (
    courseId: string,
    lessonId: string,
    progressPercentage: number,
    lastPosition?: number,
    isCompleted: boolean = false
  ) => {
    const params = new URLSearchParams();
    params.append('progress_percentage', String(progressPercentage));
    if (lastPosition !== undefined) params.append('last_position', String(lastPosition));
    params.append('is_completed', String(isCompleted));
    
    const response = await api.post(`/learn/courses/${courseId}/lessons/${lessonId}/progress?${params.toString()}`);
    return response.data;
  },

  enrollInCourse: async (courseId: string) => {
    const response = await api.post(`/learn/courses/${courseId}/enroll`);
    return response.data;
  },

  // My Courses
  getMyCourses: async (
    status?: 'in_progress' | 'completed' | 'all',
    page: number = 1,
    pageSize: number = 12
  ): Promise<CoursesListResponse> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    
    const response = await api.get(`/learn/my-courses?${params.toString()}`);
    return response.data;
  },

  // Videos (Vídeo-aulas gerenciadas no Admin)
  getVideos: async (
    search?: string,
    page: number = 1,
    pageSize: number = 12
  ): Promise<VideosListResponse> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', String(page));
    params.append('page_size', String(pageSize));

    const response = await api.get(`/learn/videos?${params.toString()}`);
    return response.data;
  },

  getVideo: async (videoId: string): Promise<VideoDetail> => {
    const response = await api.get(`/learn/videos/${videoId}`);
    return response.data;
  },

  // Materials
  getMaterials: async (
    search?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<MaterialsListResponse> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    
    const response = await api.get(`/learn/materials?${params.toString()}`);
    return response.data;
  },

  getVideoPlayback: async (videoId: string): Promise<VideoPlayback> => {
    const response = await api.get(`/learn/videos/${videoId}/playback`);
    return response.data;
  },

  // Blog
  getBlogPosts: async (
    search?: string,
    page: number = 1,
    pageSize: number = 10,
    category?: string
  ): Promise<BlogPostsListResponse> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (category) params.append('category', category);
    params.append('page', String(page));
    params.append('page_size', String(pageSize));

    const response = await api.get(`/learn/blog?${params.toString()}`);
    return response.data;
  },

  getBlogHighlights: async (): Promise<BlogHighlightsResponse> => {
    const response = await api.get('/learn/blog/highlights');
    return response.data;
  },

  getBlogCategories: async (): Promise<{ categories: BlogCategoryOption[] }> => {
    const response = await api.get('/learn/blog/categories');
    return response.data;
  },

  getBlogPost: async (slug: string): Promise<BlogPost> => {
    const response = await api.get(`/learn/blog/${slug}`);
    return response.data;
  },
};

export default learningService;

